<!--
  A view that lays out the flight details form
-->
<template>
  <div>
    <form class="flight_details_form">

      <!-- Subtitle -->
      <h2 class="subtitle">{{ $t(`${pagei18nKey}.title`) }}</h2>

      <div class="was_reported_section" v-show="reportedAtAirportEnabled">
        <!-- Reported buttons -->
        <div class="field">
          <label class="label">{{ $t(`${pagei18nKey}.incidentLabel`) }}</label>
          <ms-navigation-bar
            :buttons="[ reportedButton, unreportedButton ]"
            @navigationButtonClick="reportedButtonClick"
          />
        </div>

        <!-- Report data fields -->
        <div v-if="isReported">

          <!-- PIR number -->
          <div class="field">
            <label class="label">{{ $t(`${pagei18nKey}.pirRefLabel`) }}</label>
            <div class="control has-icons-right">
              <input
                v-model="pirReportReference"
                v-validate="{ required: true, regex: /^[a-zA-Z0-9]{5,15}$/ }"
                class="input"
                :class="{'is-danger': errors.has('pirReportReference') }"
                :data-vv-as="$t(`${pagei18nKey}.pirRef`)"
                name="pirReportReference"
                type="text"
                :placeholder="$t(`${pagei18nKey}.pirRef`)"
                autocomplete="off"
                autocapitalize="none">
              <span
                v-show="fields.pirReportReference && fields.pirReportReference.valid"
                class="icon is-small is-right">
                <i class="fas fa-check"></i>
              </span>
            </div>
            <p
              v-show="errors.has('pirReportReference')"
              class="help is-danger">
              {{ errors.first('pirReportReference') }}
            </p>
          </div>

          <!-- Date reported -->
          <div class="field">
            <label class="label">{{ $t(`${pagei18nKey}.dateReported`) }}</label>
            <ms-datetime-picker
              v-model="dateReported"
              :type="datePickerType"
              :locale="locale"
              :placeholder="$t('message.datePlaceholderText')"
              :maxDatetime="maxDatetime"/>
          </div>

          <!-- Find claim details button -->
          <AppButton
            v-if="findClaimEnabled"
            class="find_claim_button"
            @click.native="onFindClaimClick"
            :type="findClaimButton.type"
            :state="findClaimButton.state"
            :text="$t(findClaimButton.text)" />

          <!-- Find claim - In progress -->
          <div
            v-if="findClaimsState === findClaimStatesEnum.SUBMITTING"
            class="find_claim_loading">
            {{ $t(`${pagei18nKey}.flightClaimInProgress`) }}
          </div>

          <!-- find claim success -->
          <div
            v-if="findClaimsState === findClaimStatesEnum.SUBMITTED"
            class="find_claim_result">
            <div class="find_claim_message">
              <p class="find_claim_heading">
                {{ $t(`${pagei18nKey}.findClaimSuccessHeading`) }}
              </p>
              <p>{{ $t(`${pagei18nKey}.findClaimSuccessMessage`) }}</p>
            </div>
          </div>

          <!-- find claim error -->
          <div
            v-if="findClaimsState === findClaimStatesEnum.ERROR"
            class="find_claim_result">
            <div class="find_claim_message">
              <p class="find_claim_heading">
                <span class="find_claim_icon">
                  <img
                    src="@/assets/theme/images/exclamation-icon.png"
                    :alt="$t(`${pagei18nKey}.flightFinderErrorIconAlt`)">
                </span>
                {{ $t(`${pagei18nKey}.findClaimErrorHeading`) }}
              </p>
              <p>{{ $t(`${pagei18nKey}.findClaimErrorMessage`) }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="find_a_flight_section"
        v-if="!isReported || pirSubmitted || !findClaimEnabled">
      <!-- <div class="find_a_flight_section">         -->
        <!-- Booking reference -->
        <div class="field">
          <label class="label">{{ $t(`${pagei18nKey}.bookingRef`) }} <span v-if="showWorkOrder">/ {{ $t(`${pagei18nKey}.workOrderNumber`) }}</span>
               <span class="find_a_flight_error_icon">
                <img @click="openBookingRefInfo"
                  v-if="showWorkOrder"
                  src="/static/theme/img/info-icon.svg"
                  :alt="$t(`${pagei18nKey}.bookingRefIconAlt`)">
              </span>
          </label>
          <div class="control has-icons-right">
            <input
              v-model="bookingReference"
              v-validate="{ required: false }"
              class="input"
              :class="{'is-danger': errors.has('bookingReference') }"
              :data-vv-as="$t(`${pagei18nKey}.bookingRef`)"
              name="bookingReference"
              type="text"
              :placeholder="$t(`${pagei18nKey}.bookingRef`)"
              autocomplete="off"
              autocapitalize="none">
            <span
              v-show="fields.bookingReference && fields.bookingReference.valid"
              class="icon is-small is-right">
              <i class="fas fa-check"></i>
            </span>
          </div>
          <p
            v-show="errors.has('bookingReference')"
            class="help is-danger">
            {{ errors.first('bookingReference') }}
          </p>
        </div>

        <!-- Flight number -->
        <div class="field">
          <label class="label">{{ $t(`${pagei18nKey}.flightRef`) }}</label>
          <div class="control has-icons-right">
            <input
              v-model="flightReference"
              v-validate="'required'"
              class="input"
              :class="{'is-danger': errors.has('flightReference') }"
              :data-vv-as="$t(`${pagei18nKey}.flightRef`)"
              name="flightReference"
              type="text"
              :placeholder="$t(`${pagei18nKey}.flightRef`)"
              autocomplete="off"
              autocapitalize="none">
            <span
              v-show="fields.flightReference && fields.flightReference.valid"
              class="icon is-small is-right">
              <i class="fas fa-check"></i>
            </span>
          </div>
          <p
            v-show="errors.has('flightReference')"
            class="help is-danger">
            {{ errors.first('flightReference') }}
          </p>
        </div>

        <!-- Flight date -->
        <div class="field">
          <label class="label">{{ $t(`${pagei18nKey}.flightDate`) }}</label>
          <ms-datetime-picker
            v-model="flightDate"
            :type="datePickerType"
            :locale="locale"
            :placeholder="$t('message.datePlaceholderText')"
            :maxDatetime="maxDatetime"
            @input="onFlightDateInput"/>
        </div>

        <!-- Find flight button -->
        <AppButton
          v-if="findFlightEnabled"
          class="find_a_flight_button"
          @click.native="onFindFlightClick"
          :type="findAFlightButton.type"
          :state="findAFlightButton.state"
          :text="$t(findAFlightButton.text)" />

        <!-- Departure airport -->
        <div
          class="field"
          v-if="showAirportSelectionFields">
          <label class="label">{{ $t(`${pagei18nKey}.departureAirport`) }}</label>
          <p class="control is-expanded has-icons-right">
          <div class="is-fullwidth">
            <AppSelect
              v-model="departureAirport"
              v-validate:departureAirport="'required'"
              displayKey="displayKey"
              :placeholder="$t('message.selectPlaceholderText')"
              class="is-fullwidth"
              :options="airports"
              :class="{'is-danger': errors.has('departureAirport')}"
              :data-vv-as="$t(`${pagei18nKey}.departureAirport`)"
              name="departureAirport"/>
          </div>
          <p
            v-show="errors.has('departureAirport')"
            class="help is-danger">
            {{ errors.first('departureAirport') }}
          </p>
        </div>

        <!-- Arrival airport -->
        <div
          class="field"
          v-if="showAirportSelectionFields">
          <label class="label">{{ $t(`${pagei18nKey}.arrivalAirport`) }}</label>
          <p class="control is-expanded has-icons-right">
          <div class="is-fullwidth">
            <AppSelect
              v-model="arrivalAirport"
              v-validate:arrivalAirport="'required'"
              displayKey="displayKey"
              :placeholder="$t('message.selectPlaceholderText')"
              class="is-fullwidth"
              :options="airports"
              :class="{'is-danger': errors.has('arrivalAirport')}"
              :data-vv-as="$t(`${pagei18nKey}.arrivalAirport`)"
              name="arrivalAirport"/>
          </div>
          <p
            v-show="errors.has('arrivalAirport')"
            class="help is-danger">
            {{ errors.first('arrivalAirport') }}
          </p>
        </div>

        <!-- Find a flight - In progress -->
        <div
          v-if="findAFlightState === findAFlightStatesEnum.SUBMITTING"
          class="find_a_flight_loading">
          {{ $t(`${pagei18nKey}.flightFinderInProgress`) }}
        </div>

        <!-- Find a flight - Success -->
        <div
          v-if="flightData && flightData.length > 0"
          class="find_a_flight_success">
          <div class="find_a_flight_details">
            <p class="find_a_flight_success_heading">{{ $t(`${pagei18nKey}.flightFinderSuccessHeading`) }}</p>
            <p>{{ flightDate | formatFlightDate }}</p>
            <p>{{ flightDeparture }}</p>
            <p>{{ $t(`${pagei18nKey}.flightFinderSuccessMessageJoiner`) }} {{ flightArrival }}</p>
          </div>
          <div class="find_a_flight_clear">
            <span class="find_a_flight_clear_icon">
              <img
                src="@/assets/theme/images/primary-bin-icon.png"
                :alt="$t(`${pagei18nKey}.clearFlightFinderIconAlt`)">
            </span>
            <span
              @click="onClearFlightResult"
              class="find_a_flight_clear_option">
              {{ $t(`${pagei18nKey}.clearFlightFinderMessage`) }}
            </span>
          </div>
        </div>
          <!-- Find a flight - Error -->
        <div
          v-if="showFindFlightError"
          class="find_a_flight_error">
          <div class="find_a_flight_error_message">
            <p class="find_a_flight_error_heading">
              <span class="find_a_flight_error_icon">
                <img
                  src="@/assets/theme/images/exclamation-icon.png"
                  :alt="$t(`${pagei18nKey}.flightFinderErrorIconAlt`)">
              </span>
              {{ $t(`${pagei18nKey}.flightFinderErrorHeading`) }}
            </p>
            <p>{{ $t(`${pagei18nKey}.flightFinderErrorMessage`) }}</p>
          </div>
          <p class="find_a_flight_error_checkbox">
            <app-checkbox
              class="flight_checkbox"
              :uncheckedSrc="'/static/theme/img/checkbox-unchecked.png'"
              :checkedSrc="'/static/theme/img/checkbox-checked.png'"
              :selected="useCustomFlightData"
              @change="checkboxChanged"/>
            <span>{{ $t(`${pagei18nKey}.flightFinderErrorCheckboxLabel`) }}</span>
          </p>
        </div>
      </div>
    </form>

    <!-- Navigation - back and next buttons -->
    <div class="flight_details_navigation_container">
      <ms-navigation-bar
        :buttons="[ backButton, nextButton ]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>

    <!-- App message to contain Ref booking info -->
    <ms-app-message
      :title="$t(`${pagei18nKey}.bookingRefInfoHeader`)"
      :isOpen.sync="showBookingRefInfo">
      <span>
        <p>{{ $t(`${pagei18nKey}.bookingRefInfoParagraph`) }}</p>
      </span>
    </ms-app-message>
  </div>
</template>

<script>
  import api from '@/api/api'
  import {ButtonStates, ButtonTypes} from '@/components/app-button'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import MsDatetimePicker, {DatetimeTypes} from '@/components/ms-datetime-picker'
  import MsAppMessage from '@/components/ms-app-message'
  import i18n from '@/lang'
  import themeSettings from '../../theme-settings.json'
  import AppSelect from '@/components/app-select'
  import {Airport} from '@/model/airport'
  import {Address} from '@/model/address'
  import appSettings from '@/app-settings.json'

  const findAFlightStates = {
    UNSUBMITTED: 'unsubmitted',
    SUBMITTED: 'submitted',
    SUBMITTING: 'submitting',
    ERROR: 'error'
  }

  const findClaimStates = {
    UNSUBMITTED: 'unsubmitted',
    SUBMITTED: 'submitted',
    SUBMITTING: 'submitting',
    ERROR: 'error'
  }

  export default {
    name: 'FlightDetailsView',
    components: {
      MsDatetimePicker,
      MsAppMessage,
      AppSelect,
      MsNavigationBar
    },

    data: function () {
      return {
        airports: [],
        showBookingRefInfo: false,
        pagei18nKey: 'message.flightDetails',
        findClaimEnabled: themeSettings.theme.findClaimEnabled,
        reportedAtAirportEnabled: themeSettings.theme.reportedAtAirportEnabled,
        findAFlightStatesEnum: findAFlightStates,
        findClaimStatesEnum: findClaimStates,
        findAFlightState: findAFlightStates.UNSUBMITTED,
        findClaimsState: findClaimStates.UNSUBMITTED,
        isFirstFlightDateInputEvent: true,
        isFlightDateValid: false,
        isDateReportedValid: false,
        isKiosk: !!appSettings.kioskSettings
      }
    },

    mounted () {
      // if reported at airport question is disabled then we assume claim wasn't reported
      if (!this.reportedAtAirportEnabled) {
        this.isReported = false
      }
      // this.isReported ? this.findClaimsState = this.findClaimStatesEnum.UNSUBMITTED : this.findClaimsState = this.findClaimStatesEnum.NOT_REPORTED
      /**
       * Get airports list from api
       */
      api.getAirports()
        .then(response => {
          // Retrieve data and sort it
          this.airports = response.data.sort((x, y) => x.name.localeCompare(y.name))

          this.airports.forEach(airport => {
            airport.displayKey = airport.name + ' (' + airport.code + ')'
          })
        })
        .catch(console.error)
    },

    computed: {
      findFlightEnabled: {
        get () {
          return themeSettings.theme.findFlightEnabled && !this.isReported
        }
      },
      /**
       * Get date locale code
       */
      locale: {
        get () {
          const currentLanguage = this.$store.getters.language
          if (
            currentLanguage &&
            currentLanguage.dateLocaleCode
          ) {
            return currentLanguage.dateLocaleCode
          }
          return null
        }
      },

      /**
       *  Gets max datetime as epoch
       */
      maxDatetime: {
        get () {
          return new Date().getTime()
        }
      },

      bookingReference: {
        /**
         *  Gets current stored booking reference
         */
        get () {
          return this.$store.getters.bookingReference
        },

        /**
         * Sets booking reference value
         *
         * @param {string} value - Value to set booking reference to
         */
        set (value) {
          this.$store.commit('setBookingReference', value)
        }
      },

      isReported: {
        /**
         * Gets current isReported bool
         */
        get () {
          return this.$store.getters.isReported
        },

        /**
         * Sets is reported bool
         *
         * @param {boolean} value - Whether it has been reported
         */
        set (value) {
          this.$store.commit('setIsReported', value)
          // if not reported then make sure pir is cleared
          if (!value) this.$store.commit('setPirReportReference', null)
        }
      },

      pirSubmitted: {
        /**
         * Gets current isReported bool
         */
        get () {
          return this.$store.getters.pirSubmitted
        },

        /**
         * Sets is reported bool
         *
         * @param {boolean} value - Whether it has been reported
         */
        set (value) {
          this.$store.commit('setPirSubmitted', value)
        }
      },

      flightReference: {
        /**
         *  Gets current stored flight reference
         */
        get () {
          return this.$store.getters.flightReference
        },

        /**
         * Sets flight number value
         *
         * @param {string} value - Value to set flight reference to
         */
        set (value) {
          this.$store.commit('setFlightReference', value)
        }
      },

      flightDate: {
        /**
         * Gets current stored flight date
         */
        get () {
          return this.$store.getters.flightDate
        },

        /**
         * Sets flight date
         *
         * @param {number} value - Epoch date of the flight
         */
        set (value) {
          this.$store.commit('setFlightDate', value)
        }
      },

      arrivalAirport: {
        /**
         * Gets current stored arrival airport
         */
        get () {
          let selectedValue = this.$store.getters.arrivalAirport
          let index = null
          if (selectedValue) {
            index = this.getIndexOfObjectFromKey(
              selectedValue.id,
              this.airports,
              'id'
            )
          }
          return index
        },

        set (value) {
          if (!value) {
            this.$store.commit('setArrivalAirport', null)
          } else {
            this.$store.commit('setArrivalAirport', new Airport({name: value.name, id: value.id, code: value.code}))
          }
        }
      },

      departureAirport: {
        /**
         * Gets current stored departure airport
         */
        get () {
          let selectedValue = this.$store.getters.departureAirport
          let index = null
          if (selectedValue) {
            index = this.getIndexOfObjectFromKey(
              selectedValue.id,
              this.airports,
              'id'
            )
          }
          return index
        },

        set (value) {
          if (!value) {
            this.$store.commit('setDepartureAirport', null)
          } else {
            this.$store.commit('setDepartureAirport', new Airport({name: value.name, id: value.id, code: value.code}))
          }
        }
      },

      useCustomFlightData: {
        get () {
          return this.$store.getters.useCustomFlightData
        },

        set (value) {
          this.$store.commit('setUseCustomFlightData', value)
        }
      },

      flightData: {
        get () {
          return this.$store.getters.flightData
        },

        set (value) {
          this.$store.commit('setFlightData', value)
        }
      },

      pirReportReference: {
        /**
         *  Gets current stored PIR report reference
         */
        get () {
          return this.$store.getters.pirReportReference
        },

        /**
         * Sets PIR number value
         *
         * @param {string} value - Value to set PIR report reference to
         */
        set (value) {
          this.$store.commit('setIsPirPulledFromAirportReport', false)
          this.$store.commit('setPirReportReference', value)
        }
      },

      dateReported: {
        /**
         * Gets current stored date reported
         */
        get () {
          return this.$store.getters.dateReported
        },

        /**
         * Sets date reported
         *
         * @param {number} value - Epoch to set date reported to
         */
        set (value) {
          this.isDateReportedValid = (value !== null)
          this.$store.commit('setDateReported', value)
        }
      },

      /**
       * Get Find a flight button object
       */
      findAFlightButton: {
        get () {
          return {
            type: ButtonTypes.PRIMARY,
            state: this.flightFinderEnabled ? ButtonStates.ENABLED : ButtonStates.DISABLED,
            text: `${this.pagei18nKey}.findAFlightButton`
          }
        }
      },

      /**
       * Get Find a flight button object
       */
      findClaimButton: {
        get () {
          return {
            type: ButtonTypes.PRIMARY,
            state: this.claimFinderEnabled ? ButtonStates.ENABLED : ButtonStates.DISABLED,
            text: `${this.pagei18nKey}.findClaimButton`
          }
        }
      },

      /**
       * Get reported button object
       */
      reportedButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.isReported ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: 'message.yesButton',
            id: 'reportedButton'
          }
        }
      },

      /**
       * Get unreported button object
       */
      unreportedButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.isReported ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY,
            text: 'message.noButton',
            id: 'unreportedButton'
          }
        }
      },

      /**
       * Get back button object
       */
      backButton: {
        get () {
          return DefaultNavigationButtons.BACK
        }
      },

      /**
       * Get next button object
       */
      nextButton: {
        get () {
          // Clone DefaultNavigationButtons.NEXT and remove reference to it
          let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
          return Object.assign(
            {
              state: this.isFormValid ? ButtonStates.ENABLED : ButtonStates.DISABLED
            },
            defaultNextButton
          )
        }
      },

      /**
       * Get datetime type
       */
      datePickerType: {
        get () {
          return DatetimeTypes.DATE
        }
      },

      /**
       * Get flight arrival details
       */
      flightArrival: {
        get () {
          const arrival = this.flightData[0].arrival
          const time = this.getTimeFromDatetime(arrival.date)
          return `${arrival.airport.name} (${arrival.airport.iata}) ${time}`
        }
      },

      /**
       * Get flight departure details
       */
      flightDeparture: {
        get () {
          const departure = this.flightData[0].departure
          const time = this.getTimeFromDatetime(departure.date)
          return `${departure.airport.name} (${departure.airport.iata}) ${time}`
        }
      },

      /**
       * Checks whether flight finder form fields are valid
       *
       * @returns {boolean} Whether flight finder form fields are valid
       */
      flightFinderEnabled () {
        let flightFields = [ this.fields.flightReference ]
        if (!flightFields.some(field => field === undefined)) {
          const fieldsValid = flightFields.every(item => {
            return item.valid
          })
          let valid = fieldsValid && this.isFlightDateValid
          return valid
        }
        return false
      },

      claimFinderEnabled () {
        return this.fields.pirReportReference ? this.fields.pirReportReference.valid : false
      },

      /**
       * Checks whether find a flight error should be shown
       */
      showFindFlightError () {
        return (
          // Whether find a flight has an error
          this.findAFlightState === this.findAFlightStatesEnum.ERROR ||
          // Whether we've come back with stored data
          (
            this.flightReference &&
            this.flightDate &&
            this.useCustomFlightData
          )
        )
      },

      /**
      * If true then we can select airports, else airport selection is hidden
      */
      showAirportSelectionFields () {
        return !this.findFlightEnabled || this.showFindFlightError
      },

      showWorkOrder () {
        // we never show work order number on a kioks
        // and we only show it when there is a string
        if (!this.isKiosk &&
          this.$t(`${this.pagei18nKey}.workOrderNumber`) &&
          this.$t(`${this.pagei18nKey}.workOrderNumber`).length > 0) {
          return true
        } else {
          return false
        }
      },

      /**
       * Checks each form field to see if the form is valid
       *
       * @returns {boolean} Whether form is valid
       */
      isFormValid () {
        const keysAreValid = Object.keys(this.fields).every(key => this.fields[key].valid)
        const hasSelectedAirports =
          this.departureAirport !== null &&
          this.arrivalAirport !== null &&
          this.departureAirport !== this.arrivalAirport
        const hasFlightData = (
          (this.flightData && this.flightData.length > 0) ||
          (!this.findFlightEnabled && hasSelectedAirports) ||
          (this.useCustomFlightData && hasSelectedAirports))

        if (this.isReported) {
          return keysAreValid && hasFlightData && this.isDateReportedValid
        } else {
          return keysAreValid && hasFlightData
        }
      }
    },

    methods: {

      /**
       * Look up the claim details from the PIR number
       */
      onFindClaimClick: function () {
        this.findClaimsState = this.findClaimStatesEnum.SUBMITTING
        api.getAirportReport(this.pirReportReference)
          .then(response => {
            // console.log(response)
            // TODO move all this data parsing/setting to store??
            this.$store.commit('setIsPirPulledFromAirportReport', true)
            if (response.data.dateReported) {
              this.dateReported = response.data.dateReported
            }

            // set flight details
            let flight = response.data.flight
            if (flight) {
              if (flight.bookingReference) this.bookingReference = flight.bookingReference
              if (flight.flightDate) this.flightDate = flight.flightDate
              if (flight.flightReference) this.flightReference = flight.flightReference
              if (flight.arrivalAirport) {
                this.arrivalAirport = {
                  name: flight.arrivalAirport.name,
                  id: flight.arrivalAirport.id,
                  code: flight.arrivalAirport.code
                }
              }
              if (flight.departureAirport) {
                this.departureAirport = {
                  name: flight.departureAirport.name,
                  id: flight.departureAirport.id,
                  code: flight.departureAirport.code
                }
              }
            }

            // set user details
            let user = response.data.user
            if (user) {
              if (user.firstName) this.$store.commit('setFirstName', user.firstName)
              if (user.surname) this.$store.commit('setSurname', user.surname)
              if (user.email) this.$store.commit('setEmail', user.email)
              if (user.phone) this.$store.commit('setPhone', user.phone)
              // TODO add support for phone 2
            }

            // set address details
            let address = response.data.address
            if (address) {
              try {
                this.$store.commit('setAddress', new Address(address))
              } catch (ex) {
                console.error('failed to parse address from claim lookup')
              }
            }
            let tempAddress = response.data.luggageAddress
            if (tempAddress) {
              try {
                this.$store.commit('setTempAddress', new Address(tempAddress))
                this.$store.commit('setHasTempAddress', true)
              } catch (ex) {
                console.error('failed to parse tempAddress from claim lookup')
              }
            } else {
              this.$store.commit('setHasTempAddress', false)
            }
            this.findClaimsState = this.findClaimStatesEnum.SUBMITTED
            this.pirSubmitted = true
          })
          .catch(() => {
            this.findClaimsState = this.findClaimStatesEnum.ERROR
            this.pirSubmitted = true
          })
      },

      /**
       * Open booking ref info message popup
       */
      openBookingRefInfo: function () {
        this.showBookingRefInfo = true
      },

      /**
       * Look up flight api
       */
      onFindFlightClick: function () {
        this.findAFlightState = this.findAFlightStatesEnum.SUBMITTING
        this.useCustomFlightData = false

        api.getFlight(this.flightDate, this.flightReference)
          .then(response => {
            this.flightData = response

            let arrivalAirportCode = this.flightData[0].arrival.airport.iata
            let departureAirportCode = this.flightData[0].departure.airport.iata
            let flightReference = this.flightData[0].flightReference

            // Try to find airports in our local list
            let arrivalAiport = this.airports.find(airport => airport.code === arrivalAirportCode)
            let departureAirport = this.airports.find(airport => airport.code === departureAirportCode)

            if (response.length === 0) {
              this.arrivalAirport = null
              this.departureAirport = null
              this.findAFlightState = this.findAFlightStatesEnum.ERROR
            } else {
              if (arrivalAiport) {
                this.arrivalAirport = {
                  name: arrivalAiport.name,
                  id: arrivalAiport.id,
                  code: arrivalAiport.code
                }
              } else {
                this.arrivalAirport = null
              }

              if (departureAirport) {
                this.departureAirport = {
                  name: departureAirport.name,
                  id: departureAirport.id,
                  code: departureAirport.code
                }
              } else {
                this.departureAirport = null
              }

              this.flightReference = flightReference
              this.findAFlightState = this.findAFlightStatesEnum.SUBMITTED
            }
          })
          .catch(() => {
            this.arrivalAirport = null
            this.departureAirport = null
            this.findAFlightState = this.findAFlightStatesEnum.ERROR
          })
      },

      /**
       * On navigation button clicked change view
       *
       * @param {string} buttonId - The id of the clicked button
       */
      navigationButtonClick: function (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.$router.go(-1)
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          if (this.flightCheckbox) {
            this.flightData = {
              'reference': this.flightReference,
              'date': this.flightDate
            }
          }
          this.$router.push('/about')
        }
      },

      /**
       * Set isReported bool based on what button was clicked
       *
       * @param {string} buttonId - The id of the clicked button
       */
      reportedButtonClick: function (buttonId) {
        if (buttonId === this.reportedButton.id) {
          this.isReported = true
          // if user is swtiching to reported clear any flight lookups
          this.findAFlightState = this.findAFlightStatesEnum.UNSUBMITTED
          this.flightData = null
          this.useCustomFlightData = false
        } else if (buttonId === this.unreportedButton.id) {
          // Detach validated fields before v-if removes them from DOM
          // to prevent vee-validate from trying to validate non-existent fields
          this.$validator.detach('pirReportReference')
          this.isReported = false
        }
      },

      /**
       * Hide flight finder message and flight data when date changes
       */
      onFlightDateInput: function (value) {
        this.isFlightDateValid = (value !== null)
      },

      /**
       * Get formatted time
       *
       * @param {number} datetime - Epoch to get time from
       */
      getTimeFromDatetime: function (datetime) {
        const date = new Date(datetime)
        // Adds a 0 before number if less than 10 (5 => 05)
        const hours = (date.getHours() < 10 ? '0' : '') + date.getHours()
        // Adds a 0 before number if less than 10 (5 => 05)
        const mins = (date.getMinutes() < 10 ? '0' : '') + date.getMinutes()
        return `${hours}:${mins}`
      },

      /**
       * Clear flight finder data and hide message
       */
      onClearFlightResult: function () {
        this.findAFlightState = this.findAFlightStatesEnum.UNSUBMITTED
        this.flightData = null
        this.arrivalAirport = null
        this.departureAirport = null
        this.flightDate = new Date()
        this.flightReference = null
      },

      /**
       * Change value of checkbox
       *
       * @param {boolean} value - Value of checkbox
       */
      checkboxChanged: function (value) {
        this.useCustomFlightData = value
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })
        return foundIndex === -1 ? null : foundIndex
      }
    },

    filters: {
      /**
       * Format flight date for flight finder success message
       *
       * @param {Number} value - Date of flight to format
       *
       * @returns {*} Formatted date
       */
      formatFlightDate: function (value) {
        if (!value) return ''
        const date = new Date(value)
        const options = {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }

        return date.toLocaleDateString(i18n.locale, options)
      }
    }
  }
</script>

<style lang="scss">
  @import "../../assets/sass/main.scss";
  @import "flight-details.scss";
</style>
